import {
  ChangeEvent,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CircularProgress, Modal } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import InputFareHarbor from "./inputFareharbor";
import SelectCountOptions from "./selectCountOptions";
import SelectUniqueOption from "./selectUniqueOption";
import GoogleTranslationComponent from "../googleTranslate/GoogleTranslationComponent";
import {
  getCustomFieldsByTariffAndAudienceAndActivityId,
  useGetCustomFieldsByScheduleAndActivityId,
} from "@/api/traveler";
import { useTranslation } from "@/app/i18n/client";
import Close from "@/assets/images/icons/close.svg";
import CustomButton from "@/common/customElements/button";
import HexagonButton from "@/common/customElements/HexagonButton";
import {
  AbsoluteDiv,
  CustomDiv,
  InlineDiv,
  Separator,
  VerticalCenterDiv,
} from "@/common/divs";
import {
  AlmostBoldText,
  LightText,
  LightTextSpan,
  RegularText,
} from "@/common/texts";
import BookingContext from "@/context/bookingContext/bookingContext";
import { CustomField } from "@/model";
import { BookingContextType, PriceDetailType } from "@/types/components";
import {
  PRIMARY_BUTTON_COLOR,
  PRIMARY_BUTTON_COLOR_DISSABLED,
} from "@/utils/constants";
import "@/components/home/home.css";

const ModalFareharborExtraOptions: React.FC<{
  activityId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  scheduleId: number;
  tariffsArray: PriceDetailType[];
}> = ({ activityId, open, setOpen, scheduleId, tariffsArray }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [customFieldsByTariffs, setCustomFieldsByTariffs] = useState({});
  const processFinished = useRef<boolean>(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) {
      setLoading(true);
      handleCustomFieldsByTariff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loading, setLoading] = useState(false);

  const handleCustomFieldsByTariff = async () => {
    const auxCustomFieldsByTarifss: any = {};

    for (const element of tariffsArray) {
      const result = await getCustomFieldsByTariffAndAudienceAndActivityId(
        activityId,
        element.tariffId,
        element.audienceId,
      );

      auxCustomFieldsByTarifss[element.audienceName] = [];
      const finded = tariffsArray.find((el) => {
        if (el.audienceId == element.audienceId) return el;
      });

      let quantity = 0;
      if (finded?.quantity) {
        quantity = parseInt(finded.quantity);
      }
      for (let i = 0; i < quantity; i++) {
        auxCustomFieldsByTarifss[element.audienceName].push(result);
      }
    }

    setCustomFieldsByTariffs(auxCustomFieldsByTarifss);
    processFinished.current = true;
    tariffCustomFields.current = auxCustomFieldsByTarifss;
    setLoading(false);
  };

  useEffect(() => {
    if (Object.keys(customFieldsByTariffs).length > 0 && !mounted) {
      let sum = 0;
      const arrayOfCustomFields = Object.keys(customFieldsByTariffs);
      for (let i = 0; i < arrayOfCustomFields.length; i++) {
        for (
          let k = 0;
          //@ts-ignore
          k < customFieldsByTariffs[arrayOfCustomFields[i]].length;
          k++
        ) {
          for (
            let j = 0;
            //@ts-ignore
            j < customFieldsByTariffs[arrayOfCustomFields[i]][k].length;
            j++
          ) {
            if (
              //@ts-ignore
              customFieldsByTariffs[arrayOfCustomFields[i]][k][j].isRequired
            ) {
              sum++;
            }
          }
        }
      }
      setRequiredMissingTariffFields(sum);
      setMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(customFieldsByTariffs)]);

  const {
    data: customFieldsBySchedule,
    isLoading: isLoadingSchedule,
    isFetched: isFetchedSchedule,
  } = useGetCustomFieldsByScheduleAndActivityId(activityId, scheduleId, {
    query: {
      refetchOnWindowFocus: false,
    },
  });
  const { saveBookingInfo, bookingInfo } = useContext(
    BookingContext,
  ) as BookingContextType;

  const scheduleCustomFields = useRef<
    { pk: number; value: string | number | boolean }[]
  >([{ pk: 0, value: "" }]);
  const tariffCustomFields = useRef<any>();
  useEffect(() => {
    if (customFieldsBySchedule) {
      const auxScheduleFields = [];
      for (let i = 0; i < customFieldsBySchedule?.length; i++) {
        let auxObject = {
          pk: customFieldsBySchedule[i].pk as number,
          value: " " as string | number | boolean,
        };
        if (customFieldsBySchedule[i].type == "yes-no") {
          auxObject = {
            pk: customFieldsBySchedule[i].pk as number,
            value: true as string | number | boolean,
          };
        }
        if (customFieldsBySchedule[i].type == "extended-option") {
          auxObject = {
            pk: customFieldsBySchedule[i].pk as number,
            //@ts-ignore
            value: customFieldsBySchedule[i].extendedOptions[0].pk,
          };
        }
        if (customFieldsBySchedule[i].type == "count") {
          auxObject = {
            pk: customFieldsBySchedule[i].pk as number,
            value: 0,
          };
        }
        if (
          customFieldsBySchedule[i].type == "short" ||
          customFieldsBySchedule[i].type == "long"
        ) {
          auxObject = {
            pk: customFieldsBySchedule[i].pk as number,
            //@ts-ignore
            value: " ",
          };
        }
        auxScheduleFields.push(auxObject);
        scheduleCustomFields.current = auxScheduleFields;
      }
    }
  }, [customFieldsBySchedule]);

  const handleManageBooking = () => {
    let auxSchedule = [];
    if (
      scheduleCustomFields.current[0].pk == 0 &&
      scheduleCustomFields.current[0].value == ""
    ) {
      if (scheduleCustomFields.current.length == 1) {
        auxSchedule = [];
      } else {
        for (let i = 1; i < scheduleCustomFields.current.length; i++) {
          auxSchedule.push(scheduleCustomFields.current[i]);
        }
      }
    } else {
      auxSchedule = scheduleCustomFields.current;
    }

    const auxBookingInfo = {
      ...bookingInfo,
      customFields: auxSchedule,
      isFareharborActivity: true,
    };
    const newPriceDetail: PriceDetailType[] = [];
    auxBookingInfo.price_detail?.forEach((element) => {
      if (parseInt(element.quantity) > 0) {
        const find = Object.keys(customFieldsByTariffs).find((el) => {
          if (el == element.audienceName) return el;
        });
        if (find) {
          for (
            let i = 0;
            i < tariffCustomFields.current[element.audienceName].length;
            i++
          ) {
            const newArray = [];
            for (
              let k = 0;
              k < tariffCustomFields.current[element.audienceName][i].length;
              k++
            ) {
              if (
                tariffCustomFields.current[element.audienceName][i][k].value
              ) {
                newArray.push(
                  tariffCustomFields.current[element.audienceName][i][k],
                );
              }
            }
            newPriceDetail.push({
              ...element,
              customFields: newArray,
              seats: 1,
            });
          }
        } else {
          newPriceDetail.push(element);
        }
      }
    });
    auxBookingInfo.newAudienceSeats = newPriceDetail;
    saveBookingInfo(auxBookingInfo);
    router.push("/activity/" + bookingInfo.id + "/booking");
  };

  const [requiredMissingScheduledFields, setRequiredMissingScheduledFields] =
    useState<number>(0);
  const [requiredMissingTariffFields, setRequiredMissingTariffFields] =
    useState<number>(0);

  useEffect(() => {
    if (isFetchedSchedule && processFinished.current) {
      if (customFieldsBySchedule && customFieldsByTariffs) {
        if (customFieldsBySchedule.length == 0) {
          let acc = 0;
          const tarifArray = Object.keys(customFieldsByTariffs);
          for (let i = 0; i < tarifArray.length; i++) {
            //@ts-ignore
            const person = customFieldsByTariffs[tarifArray[i]];
            for (let j = 0; j < person.length; j++) {
              acc += person[j].length;
            }
          }
          if (acc == 0) {
            handleManageBooking();
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customFieldsBySchedule,
    customFieldsByTariffs,
    processFinished.current,
    isFetchedSchedule,
  ]);

  useEffect(() => {
    if (customFieldsBySchedule && requiredMissingScheduledFields == 0) {
      let sum = 0;
      for (let i = 0; i < customFieldsBySchedule.length; i++) {
        if (customFieldsBySchedule[i].isRequired) {
          sum++;
        }
      }
      setRequiredMissingScheduledFields(sum);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customFieldsBySchedule,
    tariffCustomFields.current,
    customFieldsByTariffs,
    tariffCustomFields,
  ]);

  const handleChangeSchedule = (
    e: ChangeEvent<HTMLInputElement>,
    i: number,
    field: CustomField,
  ) => {
    if (scheduleCustomFields) {
      scheduleCustomFields.current[i] = {
        pk: field.pk ?? 0,
        value: e.target.value,
      };
    }
  };

  const handleChangeTariff = (
    e: ChangeEvent<HTMLInputElement>,
    i: number,
    field: CustomField,
    customFields: string | number,
    j: number,
  ) => {
    if (tariffCustomFields.current) {
      tariffCustomFields.current[customFields][i][j] = {
        pk: field.pk ?? 0,
        value: e.target.value,
        type: field.type,
        isRequired: field.isRequired,
        title: field.title,
        name: field.name,
      };
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="centerModal filters-modal">
        {isLoadingSchedule || loading ?
          <VerticalCenterDiv left="47%">
            <CircularProgress size={50} />
          </VerticalCenterDiv>
        : <>
            <section className="modal-section-style">
              <AlmostBoldText fontSize="24px" style={{ marginTop: "0px" }}>
                <GoogleTranslationComponent text="Selecciona las opciones para continuar" />
              </AlmostBoldText>
              <AbsoluteDiv right="24px" top="24px">
                <HexagonButton
                  onClick={() => setOpen(false)}
                  width="32px"
                  backgroundColor={PRIMARY_BUTTON_COLOR}
                >
                  <Image
                    alt="close"
                    width="16"
                    height="16"
                    src={Close.src}
                    style={{
                      position: "absolute",
                      top: "20%",
                      left: "27%",
                    }}
                  />
                </HexagonButton>
              </AbsoluteDiv>
              <Separator
                style={{
                  background: PRIMARY_BUTTON_COLOR_DISSABLED,
                  opacity: "1",
                }}
              />
              {customFieldsBySchedule &&
                customFieldsBySchedule.map((field, i) => {
                  return (
                    <Fragment
                      key={`schedule-field-${field.pk ?? field.name ?? i}`}
                    >
                      {field.type == "extended-option" ?
                        <>
                          <section className="mini-modal-section">
                            <AlmostBoldText>{field.title}</AlmostBoldText>
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.isRequired && (
                              <LightTextSpan style={{ paddingLeft: "5px" }}>
                                <GoogleTranslationComponent text="required" />
                              </LightTextSpan>
                            )}
                            <SelectUniqueOption
                              elements={field.extendedOptions ?? []}
                              typeOfCustomField={"SCHEDULE"}
                              scheduleCustomFields={
                                scheduleCustomFields.current
                              }
                              index={i}
                              isRequired={field.isRequired}
                              type={"extended-option"}
                              primaryPk={field.pk}
                              setRequiredMissingScheduledFields={
                                setRequiredMissingScheduledFields
                              }
                            />
                            <Separator
                              style={{
                                background: PRIMARY_BUTTON_COLOR_DISSABLED,
                                opacity: "1",
                              }}
                            />
                          </section>
                        </>
                      : field.type == "yes-no" ?
                        <>
                          <section className="mini-modal-section">
                            <AlmostBoldText>
                              {" "}
                              <GoogleTranslationComponent
                                text={field.title ?? field.name ?? ""}
                              />
                            </AlmostBoldText>
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.isRequired && (
                              <LightTextSpan style={{ paddingLeft: "5px" }}>
                                <GoogleTranslationComponent text="required" />
                              </LightTextSpan>
                            )}
                            {field.description && (
                              <LightText>
                                <GoogleTranslationComponent
                                  text={field.description}
                                />
                              </LightText>
                            )}
                            <SelectUniqueOption
                              elements={[
                                {
                                  ...field,
                                  name: "Yes",
                                },
                                {
                                  ...field,
                                  name: "No",
                                },
                              ]}
                              typeOfCustomField={"SCHEDULE"}
                              scheduleCustomFields={
                                scheduleCustomFields.current
                              }
                              index={i}
                              type={"yes-no"}
                              setRequiredMissingScheduledFields={
                                setRequiredMissingScheduledFields
                              }
                              isRequired={field.isRequired}
                            />
                            <Separator
                              style={{
                                background: PRIMARY_BUTTON_COLOR_DISSABLED,
                                opacity: "1",
                              }}
                            />
                          </section>
                        </>
                      : field.type == "count" ?
                        <>
                          <section className="mini-modal-section">
                            <AlmostBoldText>
                              {" "}
                              <GoogleTranslationComponent
                                text={field.title ?? field.name ?? ""}
                              />
                            </AlmostBoldText>
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.isRequired && (
                              <LightTextSpan style={{ paddingLeft: "5px" }}>
                                <GoogleTranslationComponent text="required" />
                              </LightTextSpan>
                            )}
                            {field.description && (
                              <LightText>
                                <GoogleTranslationComponent
                                  text={field.description}
                                />
                              </LightText>
                            )}
                            <SelectCountOptions
                              min={field.countMin ?? 0}
                              max={field.countMax ?? 99}
                              typeOfCustomField={"SCHEDULE"}
                              scheduleCustomFields={
                                scheduleCustomFields.current
                              }
                              index={i}
                              pk={field.pk ?? 0}
                              isRequired={field.isRequired}
                              setRequiredMissingScheduledFields={
                                setRequiredMissingScheduledFields
                              }
                            />
                            <Separator
                              style={{
                                background: PRIMARY_BUTTON_COLOR_DISSABLED,
                                opacity: "1",
                              }}
                            />
                          </section>
                        </>
                      : field.type == "short" ?
                        <>
                          <section className="mini-modal-section">
                            <AlmostBoldText>
                              {" "}
                              <GoogleTranslationComponent
                                text={field.title ?? field.name ?? ""}
                              />
                            </AlmostBoldText>
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.isRequired && (
                              <LightTextSpan style={{ paddingLeft: "5px" }}>
                                (required)
                              </LightTextSpan>
                            )}
                            <div style={{ marginTop: "5px" }}>
                              <InputFareHarbor
                                i={i}
                                field={field}
                                handleChangeSchedule={handleChangeSchedule}
                                value={scheduleCustomFields?.current[
                                  i
                                ]?.value.toString()}
                                setRequiredMissingScheduledFields={
                                  setRequiredMissingScheduledFields
                                }
                              />
                            </div>
                            <Separator
                              style={{
                                background: PRIMARY_BUTTON_COLOR_DISSABLED,
                                opacity: "1",
                              }}
                            />
                          </section>
                        </>
                      : field.type == "long" ?
                        <>
                          <section className="mini-modal-section">
                            <AlmostBoldText>
                              {" "}
                              <GoogleTranslationComponent
                                text={field.title ?? field.name ?? ""}
                              />
                            </AlmostBoldText>
                            {field.offset !== 0 && (
                              <RegularText>
                                +{field.offsetFormatted}
                              </RegularText>
                            )}
                            {field.isRequired && (
                              <LightTextSpan style={{ paddingLeft: "5px" }}>
                                <GoogleTranslationComponent text="required" />
                              </LightTextSpan>
                            )}
                            <div style={{ marginTop: "5px" }}>
                              <InputFareHarbor
                                i={i}
                                field={field}
                                handleChangeSchedule={handleChangeSchedule}
                                value={scheduleCustomFields?.current[
                                  i
                                ]?.value.toString()}
                                setRequiredMissingScheduledFields={
                                  setRequiredMissingScheduledFields
                                }
                              />
                            </div>
                            <Separator
                              style={{
                                background: PRIMARY_BUTTON_COLOR_DISSABLED,
                                opacity: "1",
                              }}
                            />
                          </section>
                        </>
                      : field.type}
                    </Fragment>
                  );
                })}
              {customFieldsByTariffs &&
                Object.keys(customFieldsByTariffs).map((customFields) =>
                  // 'key' = [ [...], [...], [...] ]
                  //@ts-ignore
                  customFieldsByTariffs[customFields].map(
                    (fields: CustomField[], i: number) => {
                      //@ts-ignore
                      return (
                        <Fragment key={`tariff-group-${customFields}-${i}`}>
                          {fields.length > 0 ?
                            <>
                              {" "}
                              <div
                                className="mini-modal-section"
                                style={{
                                  textAlign: "left",
                                  textTransform: "uppercase",
                                  marginBottom: "10px",
                                }}
                              >
                                <AlmostBoldText>
                                  <GoogleTranslationComponent
                                    text={customFields}
                                  />{" "}
                                  {i + 1}
                                </AlmostBoldText>
                              </div>
                            </>
                          : null}
                          {fields.map((field: CustomField, j: number) => {
                            return (
                              <Fragment
                                key={`tariff-field-${customFields}-${i}-${field.pk ?? field.name ?? j}`}
                              >
                                {field.type == "extended-option" ?
                                  <>
                                    <section className="mini-modal-section">
                                      <AlmostBoldText>
                                        <GoogleTranslationComponent
                                          text={field.title ?? field.name ?? ""}
                                        />
                                      </AlmostBoldText>
                                      {field.offset !== 0 && (
                                        <RegularText>
                                          + {field.offsetFormatted}
                                        </RegularText>
                                      )}

                                      {field.isRequired && (
                                        <LightTextSpan
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          <GoogleTranslationComponent text="required" />
                                        </LightTextSpan>
                                      )}
                                      {field.description && (
                                        <LightText>
                                          <GoogleTranslationComponent
                                            text={field.description}
                                          />
                                        </LightText>
                                      )}
                                      <SelectUniqueOption
                                        elements={field.extendedOptions ?? []}
                                        typeOfCustomField={"TARIFF"}
                                        tariffCustomFields={
                                          tariffCustomFields.current
                                        }
                                        index={i}
                                        secondaryIndex={j}
                                        objectKey={customFields}
                                        type={"extended-option"}
                                        setRequiredMissingTariffFields={
                                          setRequiredMissingTariffFields
                                        }
                                        isRequired={field.isRequired}
                                        title={field.title}
                                        primaryPk={field.pk}
                                        name={field.name}
                                        field={field}
                                      />
                                      <Separator
                                        style={{
                                          background:
                                            PRIMARY_BUTTON_COLOR_DISSABLED,
                                          opacity: "1",
                                        }}
                                      />
                                    </section>
                                  </>
                                : field.type == "yes-no" ?
                                  <>
                                    <section className="mini-modal-section">
                                      <AlmostBoldText>
                                        <GoogleTranslationComponent
                                          text={field.title ?? field.name ?? ""}
                                        />
                                      </AlmostBoldText>
                                      {field.offset !== 0 && (
                                        <RegularText>
                                          +{field.offsetFormatted}
                                        </RegularText>
                                      )}

                                      {field.isRequired && (
                                        <LightTextSpan
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          <GoogleTranslationComponent text="required" />
                                        </LightTextSpan>
                                      )}
                                      {field.description && (
                                        <LightText>
                                          <GoogleTranslationComponent
                                            text={field.description}
                                          />
                                        </LightText>
                                      )}
                                      <SelectUniqueOption
                                        elements={[
                                          {
                                            ...field,
                                            name: "Yes",
                                          },
                                          {
                                            ...field,
                                            name: "No",
                                          },
                                        ]}
                                        typeOfCustomField={"TARIFF"}
                                        tariffCustomFields={
                                          tariffCustomFields.current
                                        }
                                        index={i}
                                        secondaryIndex={j}
                                        objectKey={customFields}
                                        type={"yes-no"}
                                        field={field}
                                        setRequiredMissingTariffFields={
                                          setRequiredMissingTariffFields
                                        }
                                        isRequired={field.isRequired}
                                        title={field.title}
                                        name={field.name}
                                      />
                                      <Separator
                                        style={{
                                          background:
                                            PRIMARY_BUTTON_COLOR_DISSABLED,
                                          opacity: "1",
                                        }}
                                      />
                                    </section>
                                  </>
                                : field.type == "count" ?
                                  <>
                                    <section className="mini-modal-section">
                                      <AlmostBoldText>
                                        <GoogleTranslationComponent
                                          text={field.title ?? field.name ?? ""}
                                        />
                                      </AlmostBoldText>
                                      {field.offset !== 0 && (
                                        <RegularText>
                                          +{field.offsetFormatted}
                                        </RegularText>
                                      )}

                                      {field.isRequired && (
                                        <LightTextSpan
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          <GoogleTranslationComponent text="required" />
                                        </LightTextSpan>
                                      )}
                                      {field.description && (
                                        <LightText>
                                          <GoogleTranslationComponent
                                            text={field.description}
                                          />
                                        </LightText>
                                      )}
                                      <SelectCountOptions
                                        min={field.countMin ?? 0}
                                        max={field.countMax ?? 99}
                                        typeOfCustomField={"TARIFF"}
                                        tariffCustomFields={
                                          tariffCustomFields.current
                                        }
                                        index={i}
                                        secondaryIndex={j}
                                        objectKey={customFields}
                                        pk={field.pk ?? 1111}
                                        type={"count"}
                                        isRequired={field.isRequired}
                                        title={field.title}
                                        name={field.name}
                                      />
                                      <Separator
                                        style={{
                                          background:
                                            PRIMARY_BUTTON_COLOR_DISSABLED,
                                          opacity: "1",
                                        }}
                                      />
                                    </section>
                                  </>
                                : field.type == "short" ?
                                  <>
                                    <section className="mini-modal-section">
                                      <AlmostBoldText>
                                        <GoogleTranslationComponent
                                          text={field.title ?? field.name ?? ""}
                                        />
                                      </AlmostBoldText>
                                      {field.offset ?
                                        field.offset !== 0 && (
                                          <RegularText>
                                            +{field.offsetFormatted}
                                          </RegularText>
                                        )
                                      : null}

                                      {field.isRequired && (
                                        <LightTextSpan
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          <GoogleTranslationComponent text="required" />
                                        </LightTextSpan>
                                      )}
                                      <div style={{ marginTop: "5px" }}>
                                        <InputFareHarbor
                                          i={i}
                                          field={field}
                                          handleChangeTariff={
                                            handleChangeTariff
                                          }
                                          j={j}
                                          customFields={customFields}
                                          value={
                                            tariffCustomFields.current[
                                              customFields
                                            ][i][j]?.value
                                          }
                                          setRequiredMissingTariffFields={
                                            setRequiredMissingTariffFields
                                          }
                                        />
                                      </div>
                                      <Separator
                                        style={{
                                          background:
                                            PRIMARY_BUTTON_COLOR_DISSABLED,
                                          opacity: "1",
                                        }}
                                      />
                                    </section>
                                  </>
                                : field.type == "long" ?
                                  <>
                                    <section className="mini-modal-section">
                                      <AlmostBoldText>
                                        <GoogleTranslationComponent
                                          text={field.title ?? field.name ?? ""}
                                        />
                                      </AlmostBoldText>
                                      {field.offset !== 0 && (
                                        <RegularText>
                                          +{field.offsetFormatted}
                                        </RegularText>
                                      )}
                                      {field.offset ?
                                        field.offset !== 0 && (
                                          <RegularText>
                                            +{field.offsetFormatted}
                                          </RegularText>
                                        )
                                      : null}

                                      {field.isRequired && (
                                        <LightTextSpan
                                          style={{ paddingLeft: "5px" }}
                                        >
                                          (required)
                                        </LightTextSpan>
                                      )}
                                      <div style={{ marginTop: "5px" }}>
                                        <InputFareHarbor
                                          i={i}
                                          field={field}
                                          handleChangeTariff={
                                            handleChangeTariff
                                          }
                                          j={j}
                                          customFields={customFields}
                                          value={
                                            tariffCustomFields.current[
                                              customFields
                                            ][i][j]?.value
                                          }
                                          setRequiredMissingTariffFields={
                                            setRequiredMissingTariffFields
                                          }
                                        />
                                      </div>
                                      <Separator
                                        style={{
                                          background:
                                            PRIMARY_BUTTON_COLOR_DISSABLED,
                                          opacity: "1",
                                        }}
                                      />
                                    </section>
                                  </>
                                : field.type}
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      );
                    },
                  ),
                )}
            </section>

            <section className="mini-modal-section">
              <InlineDiv
                style={{ gap: "20px", marginTop: "20PX", flexWrap: "wrap" }}
              >
                <CustomButton
                  width="257px"
                  height="50px"
                  backgroundColor={
                    (
                      requiredMissingScheduledFields +
                        requiredMissingTariffFields ==
                      0
                    ) ?
                      PRIMARY_BUTTON_COLOR
                    : PRIMARY_BUTTON_COLOR_DISSABLED
                  }
                  onClick={() => {
                    if (
                      requiredMissingScheduledFields +
                        requiredMissingTariffFields ==
                      0
                    ) {
                      handleManageBooking();
                    }
                  }}
                >
                  <RegularText fontSize="16px">{t("Reservar")}</RegularText>
                </CustomButton>
                <CustomDiv style={{ alignItems: "center", display: "flex" }}>
                  <RegularText fontSize="16px">
                    <GoogleTranslationComponent text="Campos requeridos por completar: " />
                    {requiredMissingScheduledFields +
                      requiredMissingTariffFields}
                  </RegularText>
                </CustomDiv>
              </InlineDiv>
            </section>
          </>
        }
      </div>
    </Modal>
  );
};

export default ModalFareharborExtraOptions;
